import { Component, Inject, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BehaviorSubject, first, catchError, EMPTY } from 'rxjs';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';
import { indicate } from 'src/app/operators';
import { MultiSelectFilterComponent } from 'src/app/search/filters/multi-select/multi-select-filter/multi-select-filter.component';
import {
  EsraPipingAPIClient,
  FilterType,
  FilterableProperty,
  PipingFilterCategory,
  PipingSearchFilterModel,
  PipingSearchResultViewModel,
  ReleaseComponent,
  ReleaseDetailViewModel,
  SearchRequest,
} from 'src/app/shared/models/autogenerated-piping';

export interface AddClassToReleaseDialogData {
  releaseComponent: ReleaseComponent;
  assignedObjectIds: string[];
  release: ReleaseDetailViewModel;
}

export interface AddClassToReleaseDialogResult {
  selectedObjectIds: string[];
}

interface DialogFilter {
  filterDisplayName: string;
  filterPropertyName: string;
  options: string[];
  filterCategory: PipingFilterCategory | null;
  filterType: string;
  displayOrder: number;
}

interface AddableObjectRow {
  id: string;
  specificationCode: string;
  businessUnit: string;
  application: string;
  service: string;
  valveType: string;
  description: string;
  ratingClass: string;
  temperatureRange: string;
  valveTrim: string;
  revision: string;
  ends: string;
  sizeRange: string;
  material: string;
  body: string;
  isAlreadyAssigned: boolean;
}

@Component({
  selector: 'app-add-class-to-release-dialog',
  templateUrl: './add-class-to-release-dialog.component.html',
  styleUrls: ['./add-class-to-release-dialog.component.scss'],
  standalone: false,
})
export class AddClassToReleaseDialogComponent {
  readonly pipeDisplayedColumns: string[] = [
    'select',
    'specificationCode',
    'businessUnit',
    'application',
    'service',
    'ratingClass',
    'temperatureRange',
    'valveTrim',
    'revision',
    'sizeRange',
    'material',
  ];

  readonly valveDisplayedColumns: string[] = [
    'select',
    'specificationCode',
    'businessUnit',
    'application',
    'valveType',
    'description',
    'ends',
    'body',
  ];

  readonly pageSizes: number[] = [10, 25, 50, 100, 500];

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  dataSource: MatTableDataSource<AddableObjectRow> = new MatTableDataSource<AddableObjectRow>([]);

  filters: DialogFilter[] = [];
  searchText: string = '';
  pageSize: number = 25;
  currentPage: number = 0;
  totalCount: number = 0;

  private readonly filterSelections: Map<string, Set<string>> = new Map<string, Set<string>>();
  private readonly selectedObjectIds: Set<string> = new Set<string>();
  private readonly assignedObjectIdsSet: Set<string>;

  /**
   * True once the sidebar filter options have been populated from the initial unfiltered search.
   * Prevents filter options from shrinking when the user narrows results by selecting a filter.
   */
  private filtersInitialized: boolean = false;

  @ViewChildren(MultiSelectFilterComponent) childFilters?: QueryList<MultiSelectFilterComponent>;

  get selectedCount(): number {
    return this.selectedObjectIds.size;
  }

  get objectTypeLabel(): string {
    return this.data.releaseComponent === ReleaseComponent._1 ? 'valve' : 'pipe class';
  }

  get isValveRelease(): boolean {
    return this.data.releaseComponent === ReleaseComponent._1;
  }

  get displayedColumns(): string[] {
    return this.isValveRelease ? this.valveDisplayedColumns : this.pipeDisplayedColumns;
  }

  /** Filters belonging to the "common" category (PipingFilterCategory._3), shown for all object types. */
  get commonFilters(): DialogFilter[] {
    return this.filters.filter((f: DialogFilter) => f.filterCategory === PipingFilterCategory._3);
  }

  /** Filters belonging to the "pipe class" category (PipingFilterCategory._1). */
  get pipeClassFilters(): DialogFilter[] {
    return this.filters.filter((f: DialogFilter) => f.filterCategory === PipingFilterCategory._1);
  }

  /** Filters belonging to the "valve" category (PipingFilterCategory._2). */
  get valveFilters(): DialogFilter[] {
    return this.filters.filter((f: DialogFilter) => f.filterCategory === PipingFilterCategory._2);
  }

  /** Flat list of every active filter value as {filterPropertyName, value} for the chip bar. */
  get activeFilterChips(): { filterPropertyName: string; displayName: string; value: string }[] {
    const chips: { filterPropertyName: string; displayName: string; value: string }[] = [];
    for (const [propertyName, values] of this.filterSelections.entries()) {
      // Look up the human-readable label from the sidebar filter list.
      const filterDef: DialogFilter | undefined = this.filters.find(
        (f: DialogFilter) => f.filterPropertyName === propertyName
      );
      const displayName: string = filterDef?.filterDisplayName ?? propertyName;
      for (const value of values) {
        chips.push({ filterPropertyName: propertyName, displayName, value });
      }
    }
    return chips;
  }

  constructor(
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly dialogRef: MatDialogRef<AddClassToReleaseDialogComponent, AddClassToReleaseDialogResult>,
    private readonly router: Router,
    private readonly snackBarService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public readonly data: AddClassToReleaseDialogData,
  ) {
    this.assignedObjectIdsSet = new Set<string>(
      (data.assignedObjectIds ?? []).map((id: string) => id.toLowerCase())
    );

    this.search();
  }

  callSearch(term: string): void {
    this.searchText = term;
    this.currentPage = 0;
    this.search();
  }

  onCheckboxChange(selectedValues: Set<string>, filter: DialogFilter): void {
    if (selectedValues.size === 0) {
      this.filterSelections.delete(filter.filterPropertyName);
    } else {
      this.filterSelections.set(filter.filterPropertyName, new Set<string>(selectedValues));
    }

    this.currentPage = 0;
    this.search();
  }

  /**
   * Uses pointerdown so removal still fires if chip internals swallow click events.
   */
  handleChipRemovePointerDown(event: PointerEvent, filterPropertyName: string, value: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.removedChip(filterPropertyName, value);
  }

  /** Removes a single chip value from the active filter, syncs the sidebar checkbox, and re-triggers search. */
  removedChip(filterPropertyName: string, value: string): void {
    const currentValues: Set<string> | undefined = this.filterSelections.get(filterPropertyName);
    if (!currentValues) {
      return;
    }

    currentValues.delete(value);
    if (currentValues.size === 0) {
      this.filterSelections.delete(filterPropertyName);
    }

    // Sync the sidebar checkbox visual state without triggering a redundant search cycle.
    this.childFilters?.find((f: MultiSelectFilterComponent) => f.propertyName === filterPropertyName)
      ?.deselectOption(value);

    this.currentPage = 0;
    this.search();
  }

  clearAllFilters(): void {
    this.childFilters?.forEach((childFilter: MultiSelectFilterComponent) => childFilter.clearSelections());
    this.filterSelections.clear();
    this.currentPage = 0;
    // Keep the first-load sidebar options stable after clearing selections.
    this.search();
  }

  updatePage(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.search();
  }

  toggleSelection(row: AddableObjectRow): void {
    if (row.isAlreadyAssigned || !row.id) {
      return;
    }

    if (this.selectedObjectIds.has(row.id)) {
      this.selectedObjectIds.delete(row.id);
    } else {
      this.selectedObjectIds.add(row.id);
    }
  }

  isSelected(row: AddableObjectRow): boolean {
    return this.selectedObjectIds.has(row.id);
  }

  openObjectDetailsInNewTab(row: AddableObjectRow): void {
    if (!row.id) {
      return;
    }

    const url: string = this.isValveRelease
      ? this.router.serializeUrl(this.router.createUrlTree(['/valvesummary', row.id]))
      : this.router.serializeUrl(this.router.createUrlTree(['/pipingsummary', row.id]));

    window.open(url, '_blank');
  }

  /**
   * Returns the count of rows that are selectable (not already assigned).
   */
  getSelectableRowCount(): number {
    return this.dataSource.data.filter((row: AddableObjectRow) => !row.isAlreadyAssigned).length;
  }

  /**
   * Returns true if all selectable rows are currently selected.
   */
  get isAllSelectableSelected(): boolean {
    const selectableRows: AddableObjectRow[] = this.dataSource.data.filter((row: AddableObjectRow) => !row.isAlreadyAssigned);
    if (selectableRows.length === 0) {
      return false;
    }
    return selectableRows.every((row: AddableObjectRow) => this.selectedObjectIds.has(row.id));
  }

  /**
   * Returns true if some (but not all) selectable rows are selected.
   */
  get isSelectAllIndeterminate(): boolean {
    const selectableRows: AddableObjectRow[] = this.dataSource.data.filter((row: AddableObjectRow) => !row.isAlreadyAssigned);
    if (selectableRows.length === 0) {
      return false;
    }
    const selectedCount: number = selectableRows.filter((row: AddableObjectRow) => this.selectedObjectIds.has(row.id)).length;
    return selectedCount > 0 && selectedCount < selectableRows.length;
  }

  /**
   * Toggles the selection state of all selectable rows.
   * Rows that are already assigned are not affected.
   */
  toggleSelectAll(): void {
    if (this.isAllSelectableSelected) {
      // Deselect all
      this.selectedObjectIds.clear();
    } else {
      // Select all selectable rows
      this.dataSource.data.forEach((row: AddableObjectRow) => {
        if (!row.isAlreadyAssigned) {
          this.selectedObjectIds.add(row.id);
        }
      });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  addSelected(): void {
    const selectedObjectIds: string[] = Array.from(this.selectedObjectIds)
      .filter((id: string) => !this.assignedObjectIdsSet.has(id.toLowerCase()));

    if (selectedObjectIds.length === 0 || !this.data.release?.id) {
      return;
    }

    this.saving$.next(true);

    // Call the API to add selected items to the release.
    // Only closes the dialog on success; keeps it open on error so user can retry.
    const addCall = this.data.releaseComponent === ReleaseComponent._1
      ? this.esraPipingAPIClient.addValvesToRelease(this.data.release.id, selectedObjectIds)
      : this.esraPipingAPIClient.addPipeClassesToRelease(this.data.release.id, selectedObjectIds);

    addCall
      .pipe(
        first(),
        catchError((error: unknown) => {
          this.saving$.next(false);
          const errorMessage: string = getApiErrorMessage(error, 'Failed to add item(s) to release. Please try again.');
          this.snackBarService.showError(errorMessage);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.saving$.next(false);
        this.snackBarService.showInfo(`Added ${selectedObjectIds.length} item(s) to release.`);
        this.dialogRef.close({ selectedObjectIds });
      });
  }

  private search(): void {
    const request: SearchRequest = new SearchRequest();
    request.page = this.currentPage;
    request.perPage = this.pageSize;
    request.mainSearch = this.buildMainSearch(this.searchText);
    request.filters = this.buildFilters();
    request.highlightMatches = false;

    this.esraPipingAPIClient.searchPipingAndValves(false, request)
      .pipe(indicate(this.loading$), first())
      .subscribe((response) => {
        this.totalCount = response.totalResults ?? 0;
        // Only update filter sidebar options on the first (unfiltered) load so that
        // options don't disappear when the user narrows results by applying a filter.
        if (!this.filtersInitialized) {
          this.updateAvailableFilters(response.pipingSearchFilters ?? []);
          this.filtersInitialized = true;
        }
        this.dataSource.data = (response.results ?? []).map((result: PipingSearchResultViewModel) => this.toRow(result));
      });
  }

  private buildMainSearch(searchText: string): string {
    const trimmedSearchText: string = (searchText ?? '').trim();
    return trimmedSearchText.length > 0 ? `${trimmedSearchText}*` : '';
  }

  private buildFilters(): FilterableProperty[] {
    const filters: FilterableProperty[] = [];

    const objectType: string = this.data.releaseComponent === ReleaseComponent._1 ? 'VALVE' : 'PIPE';
    filters.push(this.createFilterableProperty(FilterType._1, 'ObjectType', [objectType]));

    // Apply hidden filters for release's BusinessUnit and Application.
    // These are required to match release attributes and are not shown to the user.
    if (this.data.release?.businessUnit) {
      filters.push(this.createFilterableProperty(FilterType._1, 'BusinessUnit', [this.data.release.businessUnit]));
    }
    if (this.data.release?.application) {
      filters.push(this.createFilterableProperty(FilterType._1, 'Application', [this.data.release.application]));
    }

    for (const [propertyName, selectedValues] of this.filterSelections.entries()) {

      if (selectedValues.size === 0) {
        continue;
      }

      filters.push(this.createFilterableProperty(FilterType._1, propertyName, Array.from(selectedValues)));
    }

    return filters;
  }

  /** Creates a `FilterableProperty` in assignment form to avoid object-literal excess-property issues. */
  private createFilterableProperty(filterType: FilterType, propertyName: string, filterOptions: string[]): FilterableProperty {
    const filter: FilterableProperty = new FilterableProperty();
    filter.filterType = filterType;
    filter.propertyName = propertyName;
    filter.displayName = propertyName;
    filter.filterOptions = filterOptions;
    return filter;
  }

  private updateAvailableFilters(facets: PipingSearchFilterModel[]): void {
    // These properties are applied as hidden search constraints in buildFilters() and must not appear in the filter sidebar.
    // NOTE: normalizeFacetName strips non-alphanumeric chars and lowercases, so 'IsArchivedOrDraft' → 'isarchivedordraft'.
    const hiddenPropertyNames: Set<string> = new Set<string>([
      'objecttype', 'businessunit', 'application',
    ]);

    this.filters = facets
      .filter((facet: PipingSearchFilterModel) => {
        if (!facet.filterPropertyName) { return false; }
        if (hiddenPropertyNames.has(this.normalizeFacetName(facet.filterPropertyName))) { return false; }
        return Array.isArray(facet.options) && facet.options.length > 0;
      })
      .map((facet: PipingSearchFilterModel) => ({
        filterDisplayName: facet.filterDisplayName ?? facet.filterPropertyName ?? '',
        filterPropertyName: facet.filterPropertyName ?? '',
        options: [...(facet.options ?? [])].sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })),
        filterCategory: facet.filterCategory ?? null,
        filterType: facet.filterType ?? '',
        displayOrder: facet.displayOrder ?? 0,
      }))
      // Sort by displayOrder to match the same filter ordering as the admin search page.
      .sort((a: DialogFilter, b: DialogFilter) => a.displayOrder - b.displayOrder);
  }

  /** Normalizes facet names so UI allow-listing tolerates BE naming style differences. */
  private normalizeFacetName(name: string): string {
    return (name ?? '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  private toRow(result: PipingSearchResultViewModel): AddableObjectRow {
    const source: Record<string, unknown> = ((result.objectType === 'PIPE'
      ? result.pipe
      : result.valve) as unknown as Record<string, unknown>) ?? {};

    const id: string = this.toStringSafe(source['id']);
    const code: string = result.objectType === 'VALVE'
      ? this.toStringSafe(source['tag']).split('_')[0]
      : this.toStringSafe(source['name']);

    const businessUnits: string[] = Array.isArray(source['businessUnits'])
      ? (source['businessUnits'] as unknown[]).map((bu: unknown) => this.toStringSafe(bu)).filter((bu: string) => bu.length > 0)
      : [];

    // service can be an object { name: string } or a plain string.
    const serviceObj: Record<string, unknown> = (source['service'] as Record<string, unknown>) ?? {};
    const service: string = this.toStringSafe(serviceObj['name']) || this.formatDisplayValue(source['service']);

    // ratingClass can be { value: string } or a plain string depending on object type.
    const ratingClassObj: Record<string, unknown> = (source['ratingClass'] as Record<string, unknown>) ?? {};
    const ratingClass: string = this.toStringSafe(ratingClassObj['value']) || this.formatDisplayValue(source['ratingClass']);

    // temperatureRange is an object { range: string }
    const temperatureRangeObj: Record<string, unknown> = (source['temperatureRange'] as Record<string, unknown>) ?? {};
    const temperatureRange: string = this.toStringSafe(temperatureRangeObj['range']);

    // valveTrim is an object { value: string }
    const valveTrimObj: Record<string, unknown> = (source['valveTrim'] as Record<string, unknown>) ?? {};
    const valveTrim: string = this.toStringSafe(valveTrimObj['value']);

    // fromRevision is an object { version: string }
    const fromRevisionObj: Record<string, unknown> = (source['fromRevision'] as Record<string, unknown>) ?? {};
    const revision: string = this.toStringSafe(fromRevisionObj['version']);

    const valveType: string = this.formatDisplayValue(source['componentTypeString']) || this.formatDisplayValue(source['type']);
    const ends: string = this.formatDisplayValue(source['ends']) || this.formatDisplayValue(source['end']) ||
      this.formatDisplayValue(source['connectionEnd']) || this.formatDisplayValue(source['endConnection']);
    const description: string = this.formatDisplayValue(source['description']) || this.formatDisplayValue(source['purchaseDescription']);
    const body: string = this.formatDisplayValue(source['body']);

    const sizeRange: string = this.toStringSafe(source['sizeRange']) || this.toStringSafe(source['nps']);
    const material: string = this.formatDisplayValue(source['material']);

    return {
      id,
      specificationCode: code || 'N/A',
      businessUnit: businessUnits[0] ?? 'N/A',
      application: this.toStringSafe(source['application']) || 'N/A',
      service: service || 'N/A',
      valveType: valveType || 'N/A',
      ratingClass: ratingClass || 'N/A',
      temperatureRange: temperatureRange || 'N/A',
      valveTrim: valveTrim || 'N/A',
      revision: revision || 'N/A',
      ends: ends || 'N/A',
      sizeRange: sizeRange || 'N/A',
      material: material || 'N/A',
      description: description || 'N/A',
      body: body || 'N/A',
      isAlreadyAssigned: id ? this.assignedObjectIdsSet.has(id.toLowerCase()) : false,
    };
  }

  /** Normalizes heterogeneous API values (string/object/array) into a human-readable table cell text. */
  private formatDisplayValue(value: unknown): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      const items: string[] = value
        .map((item: unknown) => this.formatDisplayValue(item))
        .filter((item: string) => item.length > 0);
      return items.join(', ');
    }

    if (typeof value === 'object') {
      const source: Record<string, unknown> = value as Record<string, unknown>;
      // Prefer value-bearing fields first; some dictionaries expose `name: Material`
      // and the user-facing string in `value` (e.g., "Ductile Iron").
      const preferredKeys: string[] = ['value', 'range', 'displayName', 'description', 'name', 'code', 'id'];
      for (const key of preferredKeys) {
        const candidate: string = this.formatDisplayValue(source[key]);
        if (candidate.length > 0) {
          return candidate;
        }
      }
    }

    return '';
  }

  private toStringSafe(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (value === undefined || value === null) {
      return '';
    }

    return String(value);
  }
}
